import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface TraccarConfig {
  serverUrl: string;
  username: string;
  password?: string;
  enabled: boolean;
  lastTestedAt?: string;
  lastTestStatus?: 'success' | 'error' | 'pending';
}

export interface FleetGpsSettings {
  company_id: string;
  traccar: TraccarConfig;
}

const DEFAULT_SETTINGS: TraccarConfig = {
  serverUrl: '',
  username: '',
  password: '',
  enabled: false,
  lastTestStatus: 'pending',
};

export const useFleetGpsSettings = (companyId?: string) => {
  return useQuery({
    queryKey: ['fleet-gps-settings', companyId],
    queryFn: async (): Promise<FleetGpsSettings> => {
      if (!companyId) {
        throw new Error('Company ID é obrigatório');
      }

      const { data, error } = await supabase
        .from('fleet_gps_settings')
        .select('*')
        .eq('company_id', companyId)
        .eq('setting_group', 'traccar');

      if (error) throw error;

      // Se não houver configurações, retornar defaults
      if (!data || data.length === 0) {
        return {
          company_id: companyId,
          traccar: DEFAULT_SETTINGS,
        };
      }

      // Extrair configuração do Traccar
      const traccarConfig = data.find(s => s.setting_key === 'server_config');

      return {
        company_id: companyId,
        traccar: (traccarConfig?.setting_value ? traccarConfig.setting_value as unknown as TraccarConfig : DEFAULT_SETTINGS),
      };
    },
    enabled: !!companyId,
  });
};

export const useSaveFleetGpsSettings = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (traccarConfig: TraccarConfig) => {
      // Validações
      if (!traccarConfig.serverUrl?.trim()) {
        throw new Error('URL do servidor é obrigatória');
      }
      if (!traccarConfig.username?.trim()) {
        throw new Error('Usuário é obrigatório');
      }

      // Limpar URL removendo /api/session se existir
      let cleanUrl = traccarConfig.serverUrl.trim();
      if (cleanUrl.endsWith('/api/session')) {
        cleanUrl = cleanUrl.replace(/\/api\/session\/?$/, '');
      }
      cleanUrl = cleanUrl.replace(/\/$/, '');

      // Validar formato de URL
      try {
        new URL(cleanUrl);
      } catch {
        throw new Error('URL do servidor inválida');
      }

      // Buscar configuração existente para preservar senha se necessário
      const { data: existing } = await supabase
        .from('fleet_gps_settings')
        .select('setting_value')
        .eq('company_id', companyId)
        .eq('setting_group', 'traccar')
        .eq('setting_key', 'server_config')
        .maybeSingle();

      const prev = (existing?.setting_value ? existing.setting_value as unknown as TraccarConfig : {}) as TraccarConfig;
      
      // Merge preservando a senha se a nova vier vazia
      const merged: TraccarConfig = {
        ...prev,
        ...traccarConfig,
        serverUrl: cleanUrl,
        password: traccarConfig.password && traccarConfig.password.trim() !== '' 
          ? traccarConfig.password 
          : prev.password,
      };

      // Preparar registro
      const record = {
        company_id: companyId,
        setting_group: 'traccar',
        setting_key: 'server_config',
        setting_value: merged as any,
        description: 'Configurações do servidor Traccar',
      };

      // Upsert
      const { error } = await supabase
        .from('fleet_gps_settings')
        .upsert([record], {
          onConflict: 'company_id,setting_group,setting_key',
        });

      if (error) throw error;

      return merged;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-gps-settings', companyId] });
      toast.success('Configurações do Traccar salvas com sucesso');
    },
    onError: (error: any) => {
      const errorMessage = error.message || 'Erro ao salvar configurações do Traccar';
      toast.error(errorMessage);
      console.error('Error saving fleet GPS settings:', error);
    },
  });
};

export const useTestTraccarConnection = () => {
  return useMutation({
    mutationFn: async (credentials: { serverUrl: string; username: string; password?: string }) => {
      const { data, error } = await supabase.functions.invoke('trackingone-traccar-connection-test', {
        body: credentials,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Conexão com Traccar estabelecida com sucesso');
      } else {
        toast.error(data.message || 'Falha ao conectar com Traccar');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao testar conexão com Traccar');
      console.error('Error testing Traccar connection:', error);
    },
  });
};
